import { useMemo, useState } from "react";

import {
    calculateUnwindStats,
    getLoopSlippage,
    LoopPositionExpanded,
    LoopUnwindParams,
    useActiveWallet,
    useLoopCollateralPrice,
    useLoopUnwindTransaction,
    useUnwindQuoteAndIxs,
    getZcCollateral,
    getZcLedger,
    LoopRouteResponseExpanded
} from "@bridgesplit/abf-react";
import {
    Result,
    LOADING_ERROR,
    MISSING_WALLET_ERROR,
    bsMath,
    formatTokenAmount,
    Hidable,
    formatUsd
} from "@bridgesplit/utils";
import {
    Column,
    OutlinedCard,
    Row,
    StatColumn,
    TagTextAlert,
    Text,
    TooltipText,
    ToggleChevron,
    StatProps,
    Icon,
    Button,
    MEDIA,
    hideScrollbarSx,
    useAppPalette
} from "@bridgesplit/ui";
import { COPY } from "app/constants";
import { TrackTransactionEvent } from "app/types";
import { LoopUnwindType, TokenListMetadata } from "@bridgesplit/abf-sdk";
import { Collapse, Divider, useMediaQuery } from "@mui/material";

import { ActionProps } from "./types";
import {
    AppButton,
    Slippage,
    TokenWithDustUsd,
    useLoopUnwindAlerts,
    useLoopDisclaimers,
    LoopDisplayType,
    RefreshButton,
    TokenAmountWithUsd
} from "../../common";
import { useTransactionSender } from "../../transactions";
import { DisclaimerAlert } from "./common";

const wideRowStyle = {
    whiteSpace: "nowrap",
    overflow: "auto",
    maxWidth: "300px",
    ...hideScrollbarSx
};

const tokenFormatOptions = { compactLargeNumbers: true, short: true };
const textVariant = "body2";

export default function LoopUnwind({ loopPosition }: ActionProps) {
    const { slippagePresetsUi, defaultSlippageUi } = getLoopSlippage(loopPosition?.loopExpanded);
    const { slippagePercentDecimals, slippageWrapperProps, slippageButtonProps } = Slippage.useController({
        defaultSlippageUi,
        presetsUi: slippagePresetsUi
    });

    const { unwindType, displayType, setDisplayTypeHandler, setUnwindType } = useDisplayController();

    const unwind = useLoopUnwind();

    const {
        data: externalQuote,
        isError,
        isLoading: externalQuoteLoading,
        isFetching: externalQuoteFetching,
        refetch: refetchExternalQuote
    } = useUnwindQuoteAndIxs({
        loopPositionExpanded: loopPosition,
        slippagePercentDecimals: slippagePercentDecimals ?? 0,
        unwindType
    });

    const { data: jupiterPrices } = useLoopCollateralPrice({
        tokens: loopPosition?.loopExpanded
            ? [loopPosition.loopExpanded.collateralToken, loopPosition.loopExpanded.principalToken]
            : undefined,
        options: { skip: !loopPosition }
    });

    const summary = useMemo(() => {
        if (!loopPosition || !jupiterPrices || externalQuoteLoading || externalQuoteFetching) return undefined;
        return calculateUnwindStats({ loopPosition, externalQuote, jupiterPrices, unwindType });
    }, [loopPosition, jupiterPrices, externalQuoteLoading, externalQuote, externalQuoteFetching, unwindType]);

    const { unwindWarningMessage } = useLoopUnwindAlerts({
        loopPositionExpanded: loopPosition,
        summary,
        variant: "route"
    });

    const disclaimer = useLoopDisclaimers({ loopExpanded: loopPosition?.loopExpanded });

    const slippageButton = { ...slippageButtonProps, showSlippageValue: false };

    const { primaryToAlpha } = useAppPalette();

    return (
        <Slippage.Wrapper {...slippageWrapperProps}>
            <Row spaceBetween>
                <Text variant="h4">{COPY.UNWIND_TERM}</Text>
                <Row spacing={0.75}>
                    <Slippage.Button {...slippageButton} />
                    <RefreshButton refresh={refetchExternalQuote} />
                    <ReceiveSwapButton
                        loopPosition={loopPosition}
                        unwindType={unwindType}
                        setUnwindType={setUnwindType}
                    />
                </Row>
            </Row>

            <ReceiveStats
                summary={summary}
                setUnwindType={setUnwindType}
                unwindType={unwindType}
                loopPosition={loopPosition}
                loading={!summary}
            />
            <OutlinedCard hideBorder backgroundColor={primaryToAlpha}>
                <SecondaryStats
                    loading={!summary}
                    loopPosition={loopPosition}
                    summary={summary}
                    unwindType={unwindType}
                    slippagePercentDecimals={slippagePercentDecimals}
                    displayType={displayType}
                    setDisplayTypeHandler={setDisplayTypeHandler}
                />
            </OutlinedCard>
            {unwindWarningMessage ? (
                <Text variant="body2" color="caption">
                    {unwindWarningMessage}
                </Text>
            ) : null}
            {isError && (
                <TagTextAlert color="error" icon="warning">
                    Unable to find a swap route on Jupiter. Please check back later
                </TagTextAlert>
            )}
            <AppButton
                disabled={!summary || disclaimer?.disclaimerError() || isError}
                isTransaction
                asyncCta={{ onClickWithResult: () => unwind({ loopPosition, externalQuote, unwindType }) }}
            >
                {COPY.UNWIND_TERM}
            </AppButton>
            {disclaimer && <DisclaimerAlert disclaimer={disclaimer} />}
        </Slippage.Wrapper>
    );
}

type Summary = ReturnType<typeof calculateUnwindStats> | undefined;

function ReceiveStats({
    summary,
    unwindType,
    loopPosition,
    loading
}: {
    summary: Summary;
    setUnwindType: (unwindType: LoopUnwindType) => void;
    unwindType: LoopUnwindType;
    loopPosition: LoopPositionExpanded | undefined;
    loading: boolean;
}) {
    const tokenStats = summary?.tokenStats;

    const receiveOnCloseWarning = getReceiveOnCloseWarning({ loopPosition, unwindType });

    return (
        <Column spacing={1}>
            <Row spaceBetween>
                <Row spacing={0.5}>
                    <Text variant={textVariant} color="caption">
                        Receive on close
                    </Text>
                    <TooltipText helpText="Worst-case estimate of what you'll receive after repaying debt, including slippage and fees" />
                </Row>
            </Row>
            <Row spacing={0.5}>
                {unwindType === LoopUnwindType.Collateral ? (
                    <TokenWithDustUsd
                        variant="h3"
                        tokenSize="md"
                        amount={tokenStats?.receiveTokenAmount}
                        amountUsd={tokenStats?.receiveUsdAmount}
                        token={tokenStats?.tokenToReceive}
                        dustToken={tokenStats?.leftOverPrincipalToken}
                        dustAmount={tokenStats?.leftOverPrincipal}
                    />
                ) : (
                    <TokenAmountWithUsd
                        variant="h3"
                        tokenSize="md"
                        amount={tokenStats?.receiveTokenAmount}
                        amountUsd={tokenStats?.receiveUsdAmount}
                        token={tokenStats?.tokenToReceive}
                    />
                )}
                {!loading && receiveOnCloseWarning && (
                    <TooltipText helpText={receiveOnCloseWarning} icon={false} color="warning">
                        <Icon type="warning" />
                    </TooltipText>
                )}
            </Row>
        </Column>
    );
}

function ReceiveSwapButton({
    loopPosition,
    unwindType,
    setUnwindType
}: {
    loopPosition: LoopPositionExpanded | undefined;
    unwindType: LoopUnwindType;
    setUnwindType: (unwindType: LoopUnwindType) => void;
}) {
    const isMobile = useMediaQuery(MEDIA.SM.below);
    const swapToToken =
        unwindType === LoopUnwindType.Principal
            ? loopPosition?.loopExpanded.collateralToken
            : loopPosition?.loopExpanded.principalToken;

    let receiveText = `Receive ${swapToToken?.symbol}`;

    if (isMobile && receiveText.length > 18) {
        receiveText = `${receiveText.slice(0, 18)}...`;
    }

    return (
        <Button
            variant="text"
            color="secondary"
            noHover
            textProps={{ variant: textVariant }}
            onClick={() =>
                setUnwindType(
                    unwindType === LoopUnwindType.Principal ? LoopUnwindType.Collateral : LoopUnwindType.Principal
                )
            }
        >
            <Icon type="swap" />
            {receiveText}
        </Button>
    );
}

export function useDisplayController() {
    const isMobile = useMediaQuery(MEDIA.SM.below);

    const [unwindType, setUnwindType] = useState<LoopUnwindType>(LoopUnwindType.Collateral);
    const [displayType, setDisplayType] = useState<LoopDisplayType>(LoopDisplayType.UsdCostBasis);

    const setDisplayTypeHandler = () => {
        if (isMobile) return;
        setDisplayType(
            displayType === LoopDisplayType.TokenCostBasis
                ? LoopDisplayType.UsdCostBasis
                : LoopDisplayType.TokenCostBasis
        );
    };

    return {
        unwindType,
        setUnwindType,
        displayType,
        setDisplayTypeHandler
    };
}

function getPrincipalOnlyUnwindStats({
    loopPosition,
    displayType,
    setDisplayTypeHandler
}: {
    loopPosition: LoopPositionExpanded | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
}) {
    const stats = summarizeCollateralRepayStats(loopPosition, LoopUnwindType.Principal);
    if (!stats) return [];

    return [
        {
            caption: "Collateral",
            value: (
                <MultipleStatDisplay
                    {...stats.collateral}
                    displayType={displayType}
                    setDisplayTypeHandler={setDisplayTypeHandler}
                />
            )
        },
        {
            caption: "Repay",
            value: (
                <Row spacing={0.5}>
                    <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()}>
                        {displayType === LoopDisplayType.TokenCostBasis
                            ? formatTokenAmount(stats.repay.primaryAmount, {
                                  symbol: stats.repay.primaryToken?.symbol,
                                  ...tokenFormatOptions
                              })
                            : formatUsd(stats.repay.primaryUsdAmount)}
                    </Text>
                </Row>
            )
        }
    ];
}

function MultipleStatDisplay({
    primaryAmount,
    primaryUsdAmount,
    primaryToken,
    secondaryAmount,
    secondaryToken,
    displayType,
    setDisplayTypeHandler
}: StatDisplay) {
    return (
        <Row sx={wideRowStyle} spacing={0.5}>
            <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()}>
                {displayType === LoopDisplayType.TokenCostBasis
                    ? formatTokenAmount(primaryAmount, {
                          symbol: primaryToken?.symbol,
                          ...tokenFormatOptions
                      })
                    : formatUsd(primaryUsdAmount)}
            </Text>
            {displayType === LoopDisplayType.TokenCostBasis && secondaryAmount !== undefined && (
                <Text variant={textVariant} onClick={() => setDisplayTypeHandler()} color="caption">
                    {formatTokenAmount(secondaryAmount, {
                        symbol: secondaryToken?.symbol,
                        ...tokenFormatOptions
                    })}
                </Text>
            )}
        </Row>
    );
}

interface StatDisplay {
    primaryAmount: number | undefined;
    primaryUsdAmount: number | undefined;
    primaryToken: TokenListMetadata | undefined;
    secondaryAmount?: number | undefined;
    secondaryToken?: TokenListMetadata | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
}

function summarizeCollateralRepayStats(loopPosition: LoopPositionExpanded | undefined, unwindType: LoopUnwindType) {
    if (!loopPosition) return undefined;

    const collateral = getZcCollateral(loopPosition.loanExpanded);
    const ledger = getZcLedger(loopPosition.loanExpanded);
    const collateralAmount = collateral?.loanCollateral.amount;
    const principalPrice = loopPosition.loopExpanded.principalOracle.usdPrice;
    const principalToken = loopPosition.loopExpanded.principalToken;
    const collateralPrice = loopPosition.loopExpanded.collateralOracle.usdPrice;
    const totalDebt = ledger?.ledgerDebt.total;
    const totalDebtUsd = bsMath.mul(totalDebt, principalPrice);
    const collateralValueUsd = bsMath.mul(collateralAmount, collateralPrice);

    if (unwindType === LoopUnwindType.Principal) {
        const principalAmountOfCollateral = bsMath.div(collateralValueUsd, principalPrice);

        return {
            collateral: {
                primaryAmount: principalAmountOfCollateral,
                primaryUsdAmount: collateralValueUsd,
                primaryToken: principalToken,
                secondaryAmount: collateralAmount,
                secondaryToken: collateral?.loanCollateral.metadata
            },
            repay: {
                primaryAmount: totalDebt,
                primaryUsdAmount: totalDebtUsd,
                primaryToken: principalToken
            }
        };
    }

    const debtToRepayInDollars = bsMath.mul(principalPrice, totalDebt);
    const collateralAmountOfDebt = bsMath.div(debtToRepayInDollars, collateralPrice);

    return {
        collateral: {
            primaryAmount: collateralAmount,
            primaryUsdAmount: collateralValueUsd,
            primaryToken: collateral?.loanCollateral.metadata
        },
        repay: {
            primaryAmount: collateralAmountOfDebt,
            primaryUsdAmount: debtToRepayInDollars,
            primaryToken: loopPosition.loopExpanded.collateralToken,
            secondaryAmount: totalDebt,
            secondaryToken: principalToken
        }
    };
}

function getCollateralUnwindStats({
    loopPosition,
    displayType,
    setDisplayTypeHandler
}: {
    loopPosition: LoopPositionExpanded | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
}) {
    const stats = summarizeCollateralRepayStats(loopPosition, LoopUnwindType.Collateral);
    if (!stats) return [];

    return [
        {
            caption: "Collateral",
            value: (
                <Row spacing={0.5}>
                    <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()}>
                        {displayType === LoopDisplayType.TokenCostBasis
                            ? formatTokenAmount(stats.collateral.primaryAmount, {
                                  symbol: stats.collateral.primaryToken?.symbol,
                                  ...tokenFormatOptions
                              })
                            : formatUsd(stats.collateral.primaryUsdAmount)}
                    </Text>
                </Row>
            )
        },
        {
            caption: "Repay",
            value: (
                <MultipleStatDisplay
                    {...stats.repay}
                    displayType={displayType}
                    setDisplayTypeHandler={setDisplayTypeHandler}
                />
            )
        }
    ];
}

function calculateFairMarketValue(loopPosition: LoopPositionExpanded | undefined): number | undefined {
    const collateral = getZcCollateral(loopPosition?.loanExpanded);
    const collateralAmount = collateral?.loanCollateral.amount;
    const ledger = getZcLedger(loopPosition?.loanExpanded);
    const totalDebt = ledger?.ledgerDebt.total;

    const oraclePrincipalUsd = bsMath.mul(loopPosition?.loopExpanded.principalOracle.usdPrice, totalDebt);

    const oracleCollateralUsd = bsMath.mul(loopPosition?.loopExpanded.collateralOracle.usdPrice, collateralAmount);

    const fairMarketValue = bsMath.sub(oracleCollateralUsd, oraclePrincipalUsd);

    return fairMarketValue;
}

function SecondaryStats({
    loading,
    loopPosition,
    unwindType,
    summary,
    slippagePercentDecimals,
    displayType,
    setDisplayTypeHandler
}: {
    loading: boolean;
    loopPosition: LoopPositionExpanded | undefined;
    unwindType: LoopUnwindType;
    summary: Summary | undefined;
    slippagePercentDecimals: number | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
}) {
    const unwindStats = useMemo(() => {
        if (unwindType === LoopUnwindType.Principal) {
            return getPrincipalOnlyUnwindStats({ loopPosition, displayType, setDisplayTypeHandler });
        }
        return getCollateralUnwindStats({ loopPosition, displayType, setDisplayTypeHandler });
    }, [loopPosition, unwindType, displayType, setDisplayTypeHandler]);

    // this is the total impact of the swap, including fees, slippage, price impact, and oracle price deviation
    const executionDetails = useExecutionDetails({ loopPosition, summary, slippagePercentDecimals });

    return (
        <Column spacing={0.5}>
            <StatColumn variant={textVariant} spacing={0.5} stats={unwindStats} loading={loading} />
            <ExecutionDetails
                displayType={displayType}
                setDisplayTypeHandler={setDisplayTypeHandler}
                loopPosition={loopPosition}
                executionDetails={executionDetails}
                loading={loading}
                summary={summary}
                unwindType={unwindType}
                slippagePercentDecimals={slippagePercentDecimals}
            />
        </Column>
    );
}

interface ExecutionDetails {
    receiveUsdAmount: number;
    receiveTokenAmount: number;
    executionLossAmount: number;
    priceImpactAmount: number;
    slippageAmount: number;
    fairMarketValue: number;
    feeAmount: number;
    oraclePriceDeviation: number;
    leftOverPrincipalUsd: number;
}

function useExecutionDetails({
    loopPosition,
    summary,
    slippagePercentDecimals
}: {
    loopPosition: LoopPositionExpanded | undefined;
    summary: Summary | undefined;
    slippagePercentDecimals: number | undefined;
}): ExecutionDetails | undefined {
    // Calculate execution loss details

    const fairMarketValue = calculateFairMarketValue(loopPosition);

    const executionDetails = useMemo(() => {
        if (!loopPosition || !summary?.tokenStats || fairMarketValue === undefined) return undefined;

        const { tokenStats } = summary;

        const receiveUsdAmount = tokenStats.receiveUsdAmount;

        const receiveTokenAmount = tokenStats.receiveTokenAmount;

        // Calculate fee impact
        const feeAmount = summary.feeDecimals ? summary.feeDecimals * receiveUsdAmount : 0;

        // Calculate price impact
        const priceImpactAmount = summary.priceImpactDecimals ? summary.priceImpactDecimals * receiveUsdAmount : 0;

        const leftOverPrincipalUsd = tokenStats.leftOverPrincipal
            ? loopPosition.loopExpanded.principalOracle.getUsdAmount(tokenStats.leftOverPrincipal)
            : 0;

        // Calculate slippage
        const slippageAmount = slippagePercentDecimals ? slippagePercentDecimals * fairMarketValue : 0;

        const oraclePriceDeviation =
            fairMarketValue - receiveUsdAmount - feeAmount - priceImpactAmount - slippageAmount;

        const executionLossAmount = receiveUsdAmount - fairMarketValue;

        return {
            receiveUsdAmount,
            receiveTokenAmount,
            executionLossAmount,
            feeAmount,
            priceImpactAmount,
            slippageAmount,
            fairMarketValue,
            oraclePriceDeviation,
            leftOverPrincipalUsd
        };
    }, [loopPosition, summary, slippagePercentDecimals, fairMarketValue]);

    return executionDetails;
}

function ExecutionDetails({
    loopPosition,
    displayType,
    setDisplayTypeHandler,
    executionDetails,
    summary,
    slippagePercentDecimals,
    loading,
    unwindType
}: {
    loopPosition: LoopPositionExpanded | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
    executionDetails: ExecutionDetails | undefined;
    summary: Summary | undefined;
    slippagePercentDecimals: number | undefined;
    loading: boolean;
    unwindType: LoopUnwindType;
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const executionDetailsStats = useExecutionDetailsStats({
        loopPosition,
        summary,
        slippagePercentDecimals,
        unwindType,
        displayType,
        setDisplayTypeHandler
    });

    const executionDetailsTooltip =
        unwindType === LoopUnwindType.Collateral
            ? `Combined worst-case impact of slippage, fees, price impact, and market price on your expected return. Accounts for any additional ${loopPosition?.loopExpanded.principalToken.symbol} received`
            : `Combined worst-case impact of slippage, fees, price impact, and market price on your expected return.`;

    return (
        <Column>
            <Row sx={{ cursor: "pointer" }} spaceBetween>
                <Row spacing={1}>
                    <Text color="caption" variant={textVariant} loading={loading}>
                        Execution impact
                    </Text>

                    {!loading && <TooltipText variant={textVariant} helpText={executionDetailsTooltip} />}
                </Row>
                <ExecutionDetailsEndValue
                    loading={loading}
                    executionDetails={executionDetails}
                    isExpanded={isExpanded}
                    loopPosition={loopPosition}
                    unwindType={unwindType}
                    displayType={displayType}
                    setIsExpanded={setIsExpanded}
                    setDisplayTypeHandler={setDisplayTypeHandler}
                />
            </Row>

            <Collapse in={isExpanded}>
                <Column sx={{ mt: 1 }} spacing={1}>
                    {!loading && <Divider />}
                    <StatColumn variant={textVariant} spacing={0.5} stats={executionDetailsStats} />
                </Column>
            </Collapse>
        </Column>
    );
}

function ExecutionDetailsEndValue({
    loading,
    executionDetails,
    loopPosition,
    isExpanded,
    unwindType,
    displayType,
    setIsExpanded,
    setDisplayTypeHandler
}: {
    loading: boolean;
    executionDetails: ExecutionDetails | undefined;
    unwindType: LoopUnwindType;
    loopPosition: LoopPositionExpanded | undefined;
    isExpanded: boolean;
    displayType: LoopDisplayType;
    setIsExpanded: (isExpanded: boolean) => void;
    setDisplayTypeHandler: () => void;
}) {
    return (
        <Row spacing={1}>
            <Row spacing={1}>
                <Text
                    onClick={() => setDisplayTypeHandler()}
                    variant={textVariant}
                    loading={loading}
                    color={
                        executionDetails?.executionLossAmount && executionDetails.executionLossAmount < 0
                            ? "error"
                            : "success"
                    }
                >
                    {formatExecutionLossValue({
                        executionDetails,
                        unwindType,
                        loopPosition,
                        displayType
                    })}
                </Text>
            </Row>
            {!loading && (
                <ToggleChevron
                    onClick={() => setIsExpanded(!isExpanded)}
                    textVariant="body1"
                    sx={{ p: 0 }}
                    expanded={isExpanded}
                />
            )}
        </Row>
    );
}

function useUnwindTokens({
    loopPosition,
    unwindType
}: {
    loopPosition: LoopPositionExpanded | undefined;
    unwindType: LoopUnwindType;
}) {
    const isUnwindingToPrincipal = unwindType === LoopUnwindType.Principal;
    let token;
    let tokenPrice;
    if (isUnwindingToPrincipal) {
        token = loopPosition?.loopExpanded.principalToken;
        tokenPrice = loopPosition?.loopExpanded.principalOracle.usdPrice || 1;
    } else {
        token = loopPosition?.loopExpanded.collateralToken;
        tokenPrice = loopPosition?.loopExpanded.collateralOracle.usdPrice || 1;
    }

    return { token, tokenPrice };
}

function useExecutionDetailsStats({
    loopPosition,
    summary,
    slippagePercentDecimals,
    unwindType,
    displayType,
    setDisplayTypeHandler
}: {
    loopPosition: LoopPositionExpanded | undefined;
    summary: Summary | undefined;
    slippagePercentDecimals: number | undefined;
    unwindType: LoopUnwindType;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
}) {
    const executionDetails = useExecutionDetails({ loopPosition, summary, slippagePercentDecimals });

    const { token, tokenPrice } = useUnwindTokens({ loopPosition, unwindType });

    const swapDifferenceTooltip =
        unwindType === LoopUnwindType.Collateral
            ? `Adjustment due to difference in oracle price and swap price. You may receive ${loopPosition?.loopExpanded.principalToken.symbol} if there is a mismatch in the estimated and executed price.`
            : `Adjustment due to difference in oracle price and swap price`;
    const executionDetailsStats = useMemo(() => {
        if (!executionDetails) return [];

        const stats: Hidable<StatProps>[] = [
            {
                caption: "Fair market value",
                value: (
                    <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()}>
                        {formatTokenValue(executionDetails.fairMarketValue || 0, tokenPrice, token, displayType)}
                    </Text>
                ),
                tooltip:
                    "What your position is worth based on oracle prices - used for PnL before accounting for execution impact"
            },
            {
                caption: "Fees",
                value: (
                    <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()} color="error">
                        {formatTokenValue(-executionDetails.feeAmount, tokenPrice, token, displayType)}
                    </Text>
                ),

                tooltip: "Swap fees charged by AMMs",
                hide: !executionDetails.feeAmount
            },
            {
                caption: "Price impact",
                value: (
                    <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()} color="error">
                        {formatTokenValue(-executionDetails.priceImpactAmount, tokenPrice, token, displayType)}
                    </Text>
                ),
                hide: !executionDetails.priceImpactAmount
            },
            {
                caption: "Slippage",
                value: (
                    <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()} color="error">
                        {formatTokenValue(-executionDetails.slippageAmount, tokenPrice, token, displayType)}
                    </Text>
                ),
                hide: !executionDetails.slippageAmount
            },
            {
                caption: "Swap difference",
                value: (
                    <SwapPriceDifferenceDisplay
                        executionDetails={executionDetails}
                        loopPosition={loopPosition}
                        displayType={displayType}
                        setDisplayTypeHandler={setDisplayTypeHandler}
                    />
                ),
                tooltip: swapDifferenceTooltip,
                hide: !Math.abs(executionDetails.oraclePriceDeviation)
            },
            {
                caption: "Receive on close",
                value: (
                    <ReceiveOnCloseDisplay
                        executionDetails={executionDetails}
                        loopPosition={loopPosition}
                        displayType={displayType}
                        setDisplayTypeHandler={setDisplayTypeHandler}
                    />
                )
            }
        ].filter((s) => !s.hide);

        return stats;
    }, [executionDetails, token, tokenPrice, displayType, setDisplayTypeHandler, loopPosition, swapDifferenceTooltip]);

    return executionDetailsStats;
}

function useLoopUnwind() {
    const unwind = useLoopUnwindTransaction();
    const send = useTransactionSender();
    const { activeWallet } = useActiveWallet();
    return async ({
        loopPosition,
        externalQuote,
        unwindType
    }: {
        loopPosition: LoopPositionExpanded | undefined;
        externalQuote: LoopRouteResponseExpanded | undefined;
        unwindType: LoopUnwindType;
    }) => {
        if (!loopPosition || !loopPosition.loanExpanded || !externalQuote) return Result.errFromMessage(LOADING_ERROR);
        if (!activeWallet) return Result.errFromMessage(MISSING_WALLET_ERROR);
        const { loopExpanded, loanExpanded } = loopPosition;

        const receiveToken =
            unwindType === LoopUnwindType.Collateral ? loopExpanded.collateralToken : loopExpanded.principalToken;

        const params: LoopUnwindParams = {
            loanAddress: loanExpanded.loan.address,
            principalMint: loopExpanded.principalToken.mint,
            collateralMint: loopExpanded.collateralToken.mint,
            mintToReceive: receiveToken.mint,
            userPublicKey: activeWallet,
            swapIx: externalQuote.swapIxs,
            swapLuts: externalQuote.swapLuts,
            unwindType
        };

        return await send(unwind, params, {
            alerter: {
                generationEstimateSeconds: 8,
                showProgress: true
            },
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitCloseEarnOrder,
                params
            }
        });
    };
}

function formatExecutionLossValue({
    executionDetails,
    loopPosition,
    unwindType,
    displayType
}: {
    executionDetails: ExecutionDetails | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    unwindType: LoopUnwindType;
    displayType: LoopDisplayType;
}) {
    if (!executionDetails?.executionLossAmount) return "--";

    const tokenPrice =
        unwindType === LoopUnwindType.Principal
            ? loopPosition?.loopExpanded.principalOracle.usdPrice || 1
            : loopPosition?.loopExpanded.collateralOracle.usdPrice || 1;
    const token =
        unwindType === LoopUnwindType.Principal
            ? loopPosition?.loopExpanded.principalToken
            : loopPosition?.loopExpanded.collateralToken;

    return formatTokenValue(executionDetails.executionLossAmount, tokenPrice, token, displayType);
}

function formatTokenValue(
    usdAmount: number,
    tokenPrice: number,
    token: TokenListMetadata | undefined,
    displayType?: LoopDisplayType
) {
    if (displayType === LoopDisplayType.UsdCostBasis) {
        return formatUsd(usdAmount);
    }

    return formatTokenAmount(bsMath.div(usdAmount, tokenPrice), {
        symbol: token?.symbol,
        ...tokenFormatOptions
    });
}

function getReceiveOnCloseWarning({
    loopPosition,
    unwindType
}: {
    loopPosition: LoopPositionExpanded | undefined;
    unwindType: LoopUnwindType;
}): string | undefined {
    let receiveOnCloseWarning: string | undefined;

    if (unwindType === LoopUnwindType.Principal) {
        receiveOnCloseWarning = `Closing to ${loopPosition?.loopExpanded.principalToken.symbol} increases execution impact`;
    }

    return receiveOnCloseWarning;
}

function SwapPriceDifferenceDisplay({
    executionDetails,
    loopPosition,
    displayType,
    setDisplayTypeHandler
}: {
    executionDetails: ExecutionDetails | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
}) {
    if (!executionDetails) return <Text>--</Text>;

    if (displayType === LoopDisplayType.UsdCostBasis) {
        // For USD display, combine everything into a single USD value
        return (
            <Text
                variant={textVariant}
                translate="no"
                onClick={() => setDisplayTypeHandler()}
                color={executionDetails.oraclePriceDeviation <= 0 ? "success" : "error"}
            >
                {formatUsd(-executionDetails.oraclePriceDeviation)}
            </Text>
        );
    }

    // For TokenCostBasis, show the breakdown of collateral impact and principal leftover
    const collateralToken = loopPosition?.loopExpanded.collateralToken;
    const collateralPrice = loopPosition?.loopExpanded.collateralOracle.usdPrice || 1;
    const principalToken = loopPosition?.loopExpanded.principalToken;
    const principalPrice = loopPosition?.loopExpanded.principalOracle.usdPrice || 1;

    // Calculate the collateral impact (negative oracle price deviation minus leftover principal)
    const collateralImpactUsd = -executionDetails.oraclePriceDeviation - executionDetails.leftOverPrincipalUsd;
    const collateralImpactAmount = bsMath.div(collateralImpactUsd, collateralPrice);

    const leftOverPrincipalAmount = bsMath.div(executionDetails.leftOverPrincipalUsd, principalPrice);

    return (
        <Row sx={wideRowStyle} spacing={0.5}>
            <Text
                variant={textVariant}
                translate="no"
                onClick={() => setDisplayTypeHandler()}
                color={collateralImpactAmount >= 0 ? "success" : "error"}
            >
                {formatTokenAmount(collateralImpactAmount, {
                    symbol: collateralToken?.symbol,
                    ...tokenFormatOptions
                })}
            </Text>
            {leftOverPrincipalAmount > 0 && (
                <Text variant={textVariant} onClick={() => setDisplayTypeHandler()} color="success">
                    {"+ "}
                    {formatTokenAmount(leftOverPrincipalAmount, {
                        symbol: principalToken?.symbol,
                        ...tokenFormatOptions
                    })}
                </Text>
            )}
        </Row>
    );
}

function ReceiveOnCloseDisplay({
    executionDetails,
    loopPosition,
    displayType,
    setDisplayTypeHandler
}: {
    executionDetails: ExecutionDetails | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
}) {
    const { token } = useUnwindTokens({ loopPosition, unwindType: LoopUnwindType.Collateral });

    if (!executionDetails) return <Text>--</Text>;

    if (displayType === LoopDisplayType.UsdCostBasis) {
        // For USD display, show the total receive amount in USD
        return (
            <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()}>
                {formatUsd(executionDetails.receiveUsdAmount || 0)}
            </Text>
        );
    }

    // For TokenCostBasis, show the main receive amount and any leftover principal
    const principalToken = loopPosition?.loopExpanded.principalToken;
    const principalPrice = loopPosition?.loopExpanded.principalOracle.usdPrice || 1;
    const leftOverPrincipalAmount = bsMath.div(executionDetails.leftOverPrincipalUsd, principalPrice);

    return (
        <Row sx={wideRowStyle} spacing={0.5}>
            <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()}>
                {formatTokenAmount(executionDetails.receiveTokenAmount, {
                    symbol: token?.symbol,
                    ...tokenFormatOptions
                })}
            </Text>
            {leftOverPrincipalAmount > 0 && (
                <Text variant={textVariant} onClick={() => setDisplayTypeHandler()}>
                    {"+ "}
                    {formatTokenAmount(leftOverPrincipalAmount, {
                        symbol: principalToken?.symbol,
                        ...tokenFormatOptions
                    })}
                </Text>
            )}
        </Row>
    );
}
